import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { FaUsers, FaCheckCircle, FaTimesCircle, FaPlus } from 'react-icons/fa';
import { Share2, Megaphone, Mail, Percent, Award, Settings, Target, Pencil, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useMarketingDashboard, MARKETING_TIMEFRAMES, marketingTimeframeLabel } from '../../hooks/useMarketingDashboard';
import { useDevMode } from '../../hooks/useDevMode';
import DevModePanel from '../dev/DevModePanel';
import ConnectJobTreadHero from '../portal/ConnectJobTreadHero';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import MetricWidget from '../finance/MetricWidget';
import { loadLeadsCalculatorData, loadLeadsCalculatorDataForUser, saveLeadsCalculatorData, saveLeadsCalculatorDataForUser } from '../../services/leadsCalculatorService';
import { saveJobTreadLeadPreferences, saveJobTreadFieldChoice, resolveJobTreadFieldMappings } from '../../services/jobtreadFieldMapping';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

const LEADS_COLOR = '#dc2626'; // red-600 — marketing brand accent

const toNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const ConnectionPill = ({ connected, label }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
      connected
        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
    }`}
  >
    {connected ? <FaCheckCircle className="text-xs" /> : <FaTimesCircle className="text-xs" />}
    {label}
  </span>
);

const JT_HERO_DESCRIPTION =
  'Your leads, lead sources, and qualified-lead rate all flow straight from your JobTread account — live, with nothing to enter by hand.';

// In-card shortcut into the lead settings modal — empty states that depend on
// a mapped JobTread field get a real button instead of "use the gear above".
const LeadSettingsButton = ({ onClick, label = 'Open lead settings' }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 mt-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 dark:text-red-300 dark:bg-red-900/20 dark:hover:bg-red-900/40 transition-colors"
  >
    <Settings className="w-3.5 h-3.5" />
    {label}
  </button>
);

/** Orange "JobTread" source pill with a check — matches the card source badges. */
const JtPill = ({ connected = true }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
      connected
        ? 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
    }`}
  >
    {connected ? <FaCheckCircle className="text-[9px]" /> : <FaTimesCircle className="text-[9px]" />}
    JobTread
  </span>
);

// Rank color scale for the top-5 lead sources: strongest red → lightest.
const RANK_BADGES = [
  'bg-red-600 text-white',
  'bg-red-500 text-white',
  'bg-red-400 text-white',
  'bg-red-300 text-red-900',
  'bg-red-200 text-red-900'
];
const RANK_FILLS = ['#dc2626', '#ef4444', '#f87171', '#fca5a5', '#fecaca'];

/** Ranked 1–5 lead-source list with color-scaled rank badges and bars. Rows
 *  flex to fill the card's height, so fewer sources render larger. */
const TopLeadSources = ({ sources, attributed }) => {
  const top = sources.slice(0, 5);
  const maxCount = top[0]?.count || 1;
  if (!top.length) return null;
  // Fewer sources → give the rank badge, text, and bar more presence.
  const big = top.length <= 3;
  return (
    <div className="h-full flex flex-col gap-2">
      {top.map((s, i) => (
        <div key={s.name} className="flex-1 flex flex-col justify-center min-h-0">
          <div className={`flex items-center justify-between ${big ? 'text-sm' : 'text-xs'} mb-1.5`}>
            <span className="flex items-center gap-2 min-w-0">
              <span className={`${big ? 'w-6 h-6 text-xs' : 'w-[18px] h-[18px] text-[10px]'} shrink-0 rounded-full flex items-center justify-center font-bold ${RANK_BADGES[i]}`}>
                {i + 1}
              </span>
              <span className="truncate font-medium text-gray-700 dark:text-gray-200">{s.name}</span>
            </span>
            <span className="font-semibold text-gray-500 dark:text-gray-400 shrink-0 ml-2">
              {s.count} · {Math.round((s.count / (attributed || 1)) * 100)}%
            </span>
          </div>
          <div className={`${big ? 'h-2.5' : 'h-1.5'} rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden`}>
            <div
              className="h-full rounded-full"
              style={{ width: `${(s.count / maxCount) * 100}%`, backgroundColor: RANK_FILLS[i] }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// userId/accountId scope the analytics to a specific client (CRM Portal View
// embedding); when omitted, data loads for the logged-in portal user.
const MarketingAnalyticsTab = ({ userId = null, accountId = null } = {}) => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  // The uid whose data this tab reads/writes — an explicit override or the viewer.
  const targetUid = userId || currentUser?.uid || null;

  const [timeframe, setTimeframe] = useState('ytd');
  const {
    loading: dataLoading,
    refresh,
    jobtreadConnected,
    jobtreadError,
    asOfDate,
    metrics,
    sales,
    leadSettings,
    qualified,
    leadsActual,
    debug
  } = useMarketingDashboard(timeframe, { userId, accountId });
  const { devMode } = useDevMode();

  // Manual leads entries (fallback when JobTread isn't supplying leads)
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [leadsEntries, setLeadsEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    leadsCount: '',
    month: new Date().toLocaleString('default', { month: 'long' }),
    year: new Date().getFullYear().toString()
  });

  // Qualified lead % + saved calculator inputs (users/{uid}.leadsCalculator).
  // Read-only on this page — set CRM-side via the leads calculator.
  const [savedCalc, setSavedCalc] = useState(null);
  const [qualifiedPct, setQualifiedPct] = useState('');

  // Revenue goal — client-editable target that drives the Leads Needed calc.
  const [revenueGoal, setRevenueGoal] = useState(null);
  const [editingRevenue, setEditingRevenue] = useState(false);
  const [revenueDraft, setRevenueDraft] = useState('');
  const [revenueSaving, setRevenueSaving] = useState(false);

  // Channel-total inline editors (Social / Paid / Newsletter "Update")
  const [editingChannel, setEditingChannel] = useState(null); // marketingChannelTotals key
  const [channelDraft, setChannelDraft] = useState('');
  const [channelSaving, setChannelSaving] = useState(false);

  // Lead settings modal (what counts as a lead + status + lead-source field)
  const [showLeadSettings, setShowLeadSettings] = useState(false);
  const [ldEntity, setLdEntity] = useState('job'); // 'job' | 'customer'
  const [ldScope, setLdScope] = useState('all'); // 'all' | 'status'
  const [ldStatusFieldId, setLdStatusFieldId] = useState(''); // '' = built-in status
  const [ldStatuses, setLdStatuses] = useState([]);
  const [ldSourceFieldId, setLdSourceFieldId] = useState('');
  const [ldQualifiedFieldId, setLdQualifiedFieldId] = useState('');
  const [leadSettingsSaving, setLeadSettingsSaving] = useState(false);
  // Fresh field definitions fetched live when the modal opens (the hook's
  // copy can lag behind the client's current JobTread configuration).
  const [freshMapping, setFreshMapping] = useState(null);
  const [fieldsLoading, setFieldsLoading] = useState(false);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  useEffect(() => {
    if (!targetUid) return;
    (async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', targetUid));
        if (userDoc.exists() && userDoc.data().marketingLeads) {
          setLeadsEntries(userDoc.data().marketingLeads);
        } else {
          setLeadsEntries([]);
        }
      } catch (error) {
        console.error('Error loading leads entries:', error);
      }
      try {
        const saved = userId
          ? await loadLeadsCalculatorDataForUser(targetUid)
          : await loadLeadsCalculatorData();
        if (saved) {
          setSavedCalc(saved);
          const q = toNum(saved.formData?.qualifiedLeadPercent);
          if (q != null) setQualifiedPct(q);
          const r = toNum(saved.formData?.revenue);
          if (r != null) setRevenueGoal(r);
        }
      } catch (error) {
        console.error('Error loading leads calculator data:', error);
      }
    })();
  }, [currentUser, targetUid, userId]);

  /* -------- Leads needed (annual / quarter / month / week) --------
     Revenue goal is the client's editable target (falls back to sales data /
     saved input); average job size and close ratio come from the company's
     sales data (JobTread) with saved inputs as fallback; qualified lead %
     comes from a mapped JobTread field when available, else the saved value. */
  const effectiveQualified = qualified?.source === 'jobtread' ? qualified.pct : toNum(qualifiedPct);
  const revenueGoalValue = revenueGoal ?? sales?.revenue ?? toNum(savedCalc?.formData?.revenue);
  const calcInputs = useMemo(() => ({
    revenue: revenueGoalValue,
    avgJobSize: sales?.avgJobSize ?? toNum(savedCalc?.formData?.averageJobSize),
    closeRatio: sales?.closeRatio ?? toNum(savedCalc?.formData?.closeRatio)
  }), [revenueGoalValue, sales, savedCalc]);

  const computeLeadsNeeded = (qPct) => {
    const { revenue, avgJobSize, closeRatio } = calcInputs;
    const q = toNum(qPct);
    if (!revenue || !avgJobSize || !closeRatio || !q) return null;
    const contractsNeeded = Math.ceil(revenue / avgJobSize);
    const proposalsNeeded = Math.ceil(contractsNeeded / (closeRatio / 100));
    const leadsNeededAnnually = Math.ceil(proposalsNeeded / (q / 100));
    return {
      contractsNeeded,
      proposalsNeeded,
      leadsNeededAnnually,
      leadsNeededQuarterly: Math.ceil(leadsNeededAnnually / 4),
      leadsNeededMonthly: Math.ceil(leadsNeededAnnually / 12),
      leadsNeededWeekly: Math.ceil(leadsNeededAnnually / 52)
    };
  };

  const liveLeadsNeeded = useMemo(
    () => computeLeadsNeeded(effectiveQualified),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calcInputs, effectiveQualified]
  );

  // The saved calculator persists its last-computed results. avgJobSize and
  // close ratio come from the slow JobTread hook, so on page entry the live
  // recompute isn't ready yet — show the saved results immediately, then let
  // the live figures take over once the inputs arrive.
  const savedLeadsNeeded = useMemo(() => {
    const r = savedCalc?.results;
    if (!r || !(r.leadsNeededAnnually > 0)) return null;
    return {
      contractsNeeded: r.contractsNeeded,
      proposalsNeeded: r.proposalsNeeded,
      leadsNeededAnnually: r.leadsNeededAnnually,
      leadsNeededQuarterly: r.leadsNeededQuarterly,
      leadsNeededMonthly: r.leadsNeededMonthly,
      leadsNeededWeekly: r.leadsNeededWeekly
    };
  }, [savedCalc]);

  const leadsNeeded = liveLeadsNeeded || savedLeadsNeeded;

  /* -------- Revenue goal: pencil-edit in the Leads Needed header -------- */
  const handleSaveRevenue = async () => {
    const n = parseFloat(revenueDraft);
    if (!Number.isFinite(n) || n <= 0) {
      alert('Enter a revenue goal greater than 0.');
      return;
    }
    if (!currentUser) return;
    setRevenueSaving(true);
    setRevenueGoal(n);
    setEditingRevenue(false);
    try {
      const nextForm = {
        revenue: n,
        averageJobSize: calcInputs.avgJobSize ?? savedCalc?.formData?.averageJobSize ?? 0,
        closeRatio: calcInputs.closeRatio ?? savedCalc?.formData?.closeRatio ?? 0,
        qualifiedLeadPercent: effectiveQualified ?? savedCalc?.formData?.qualifiedLeadPercent ?? 0
      };
      const { revenue, avgJobSize, closeRatio } = { revenue: n, avgJobSize: calcInputs.avgJobSize, closeRatio: calcInputs.closeRatio };
      let nextResults = {};
      const q = toNum(effectiveQualified);
      if (revenue && avgJobSize && closeRatio && q) {
        const contractsNeeded = Math.ceil(revenue / avgJobSize);
        const proposalsNeeded = Math.ceil(contractsNeeded / (closeRatio / 100));
        const annual = Math.ceil(proposalsNeeded / (q / 100));
        nextResults = {
          contractsNeeded, proposalsNeeded,
          leadsNeededAnnually: annual,
          leadsNeededQuarterly: Math.ceil(annual / 4),
          leadsNeededMonthly: Math.ceil(annual / 12),
          leadsNeededWeekly: Math.ceil(annual / 52)
        };
      }
      const saved = userId
        ? await saveLeadsCalculatorDataForUser(targetUid, nextForm, nextResults)
        : await saveLeadsCalculatorData(nextForm, nextResults);
      setSavedCalc(saved);
    } catch (error) {
      console.error('Error saving revenue goal:', error);
    } finally {
      setRevenueSaving(false);
    }
  };

  /* -------- Channel totals: click Update on a card, save a new number -------- */
  const handleSaveChannel = async (key) => {
    const n = parseFloat(channelDraft);
    if (!Number.isFinite(n) || n < 0) {
      alert('Enter a valid number.');
      return;
    }
    if (!currentUser) return;
    setChannelSaving(true);
    try {
      const userDocRef = doc(db, 'users', targetUid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        await updateDoc(userDocRef, { [`marketingChannelTotals.${key}`]: n });
      } else {
        await setDoc(userDocRef, { marketingChannelTotals: { [key]: n } });
      }
      setEditingChannel(null);
      setChannelDraft('');
      refresh();
    } catch (error) {
      console.error('Error saving channel total:', error);
      alert('Error saving. Please try again.');
    } finally {
      setChannelSaving(false);
    }
  };

  /* -------- Lead settings: entity + scope + status field + lead-source field -------- */
  // Field definitions: the live fetch done at modal open wins; the hook's
  // merged list (definitions + fields observed on actual leads) fills gaps.
  const allCustomFields = (() => {
    const fresh = freshMapping?.availableCustomFields || [];
    const fromHook = leadSettings?.availableCustomFields || [];
    const freshIds = new Set(fresh.map((f) => f.id));
    return [...fresh, ...fromHook.filter((f) => !freshIds.has(f.id))];
  })();
  // JobTread custom fields target either jobs or accounts (customers); prefer
  // the ones that belong to the chosen entity, but never filter down to an
  // empty list — orgs whose targetType values differ from ours would
  // otherwise see no fields at all even though the fields exist.
  const entityTargetTypes = ldEntity === 'customer' ? ['account', 'customer'] : ['job'];
  const entityMatched = allCustomFields.filter(
    (f) => !f.targetType || entityTargetTypes.includes(String(f.targetType).toLowerCase())
  );
  const entityCustomFields = entityMatched.length > 0 ? entityMatched : allCustomFields;
  // Distinct status values for the selected status field — only meaningful for
  // the currently-loaded entity (values are computed from its leads).
  const entityMatchesLoaded = ldEntity === (leadSettings?.entity || 'job');
  const statusValueOptions = entityMatchesLoaded
    ? (leadSettings?.statusValues?.[ldStatusFieldId || ''] || [])
    : [];

  const openLeadSettings = () => {
    setLdEntity(leadSettings?.entity || 'job');
    setLdScope(leadSettings?.scope || 'all');
    setLdStatusFieldId(leadSettings?.statusFieldId || '');
    setLdStatuses((leadSettings?.statuses || []).map((s) => String(s).toLowerCase()));
    setLdSourceFieldId(leadSettings?.leadSourceField?.fieldId || '');
    setLdQualifiedFieldId(leadSettings?.qualifiedField?.fieldId || '');
    setShowLeadSettings(true);
    // Pull the client's current custom-field definitions straight from
    // JobTread so the pickers reflect their org right now, not a cache. The
    // query must run under the target user's grant (Portal View shows a
    // client whose grant the viewer doesn't hold) — the server resolves it.
    if (currentUser && leadSettings?.orgId) {
      setFieldsLoading(true);
      resolveJobTreadFieldMappings(targetUid, leadSettings.orgId, { forceRefresh: true, targetUserId: targetUid })
        .then(({ mapping }) => {
          if (mapping) setFreshMapping(mapping);
        })
        .catch((e) => console.warn('Lead settings field refresh failed:', e))
        .finally(() => setFieldsLoading(false));
    }
  };

  const handleSaveLeadSettings = async () => {
    if (!currentUser) return;
    if (ldScope === 'status' && ldStatuses.length === 0) {
      alert('Select at least one status, or choose "all" instead.');
      return;
    }
    setLeadSettingsSaving(true);
    try {
      await saveJobTreadLeadPreferences(targetUid, {
        leadEntity: ldEntity,
        leadScope: ldScope,
        statusFieldId: ldStatusFieldId,
        statuses: ldStatuses
      });
      const mappingToSave = freshMapping || leadSettings?.mapping;
      if (mappingToSave) {
        const findField = (id) => (id ? allCustomFields.find((f) => f.id === id) || null : null);
        if (ldSourceFieldId !== (leadSettings?.leadSourceField?.fieldId || '')) {
          await saveJobTreadFieldChoice(targetUid, mappingToSave, 'leadSource', findField(ldSourceFieldId));
        }
        if (ldQualifiedFieldId !== (leadSettings?.qualifiedField?.fieldId || '')) {
          await saveJobTreadFieldChoice(targetUid, mappingToSave, 'qualifiedLead', findField(ldQualifiedFieldId));
        }
      }
      setShowLeadSettings(false);
      refresh();
    } catch (error) {
      console.error('Error saving lead settings:', error);
      alert('Error saving lead settings. Please try again.');
    } finally {
      setLeadSettingsSaving(false);
    }
  };

  // Changing entity invalidates status/source field choices tied to the old
  // entity's custom fields.
  const chooseEntity = (entity) => {
    setLdEntity(entity);
    setLdStatusFieldId('');
    setLdStatuses([]);
    setLdSourceFieldId('');
    setLdQualifiedFieldId('');
  };

  /* -------- Manual leads entries CRUD (unchanged behavior) -------- */
  const handleAddEntry = async (e) => {
    e.preventDefault();
    if (!formData.leadsCount || !formData.month || !formData.year) {
      alert('Please fill in all fields');
      return;
    }
    if (!currentUser) return;
    try {
      const newEntry = {
        id: Date.now(),
        count: parseInt(formData.leadsCount, 10),
        month: formData.month,
        year: parseInt(formData.year, 10),
        createdAt: new Date().toISOString()
      };
      setLeadsEntries((prev) => [...prev, newEntry]);
      const userDocRef = doc(db, 'users', targetUid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const currentLeads = userDoc.data().marketingLeads || [];
        await updateDoc(userDocRef, { marketingLeads: [...currentLeads, newEntry] });
      } else {
        await setDoc(userDocRef, { marketingLeads: [newEntry] });
      }
      setShowAddPopup(false);
      setFormData({
        leadsCount: '',
        month: new Date().toLocaleString('default', { month: 'long' }),
        year: new Date().getFullYear().toString()
      });
      refresh();
    } catch (error) {
      console.error('Error adding entry:', error);
      alert('Error adding entry. Please try again.');
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setFormData({
      leadsCount: entry.count.toString(),
      month: entry.month,
      year: entry.year.toString()
    });
  };

  const handleUpdateEntry = async (e) => {
    e.preventDefault();
    if (!formData.leadsCount || !formData.month || !formData.year) {
      alert('Please fill in all fields');
      return;
    }
    if (!currentUser) return;
    try {
      const updatedEntry = {
        ...editingEntry,
        count: parseInt(formData.leadsCount, 10),
        month: formData.month,
        year: parseInt(formData.year, 10),
        updatedAt: new Date().toISOString()
      };
      setLeadsEntries((prev) => prev.map((entry) => (entry.id === editingEntry.id ? updatedEntry : entry)));
      const userDocRef = doc(db, 'users', targetUid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const currentLeads = userDoc.data().marketingLeads || [];
        const updatedLeads = currentLeads.map((entry) => (entry.id === editingEntry.id ? updatedEntry : entry));
        await updateDoc(userDocRef, { marketingLeads: updatedLeads });
      }
      setEditingEntry(null);
      setFormData({
        leadsCount: '',
        month: new Date().toLocaleString('default', { month: 'long' }),
        year: new Date().getFullYear().toString()
      });
      refresh();
    } catch (error) {
      console.error('Error updating entry:', error);
      alert('Error updating entry. Please try again.');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!currentUser) return;
    try {
      setLeadsEntries((prev) => prev.filter((entry) => entry.id !== entryId));
      const userDocRef = doc(db, 'users', targetUid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const currentLeads = userDoc.data().marketingLeads || [];
        await updateDoc(userDocRef, { marketingLeads: currentLeads.filter((entry) => entry.id !== entryId) });
      }
      refresh();
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Error deleting entry. Please try again.');
    }
  };

  const m = metrics;
  const showLeadsManualEntry = m.leads.source !== 'jobtread';

  // Data-quality notice on the qualified rate (only when derived from JobTread).
  const qualifiedNotice =
    qualified?.source === 'jobtread' && qualified.pct != null
      ? qualified.pct < 25
        ? 'This looks low — confirm your team is marking qualified leads in JobTread.'
        : qualified.pct > 70
          ? 'This looks high — confirm your team is marking qualified leads in JobTread.'
          : null
      : null;

  const leadsSeries = m.leads.series || [];
  // Count line for the header (drop the trailing timeframe tag from the sub).
  const leadsMeta = (m.leads.sub || '')
    .replace(` · ${marketingTimeframeLabel(timeframe)}`, '')
    .replace(`${marketingTimeframeLabel(timeframe)} · `, '')
    .trim();

  // Every metric here is JobTread-sourced; when it's not connected show the
  // connect CTA instead of empty cards (same as sales/finance analytics).
  const showConnectHero = !dataLoading && !jobtreadConnected;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Your live marketing picture, as of {asOfDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ConnectionPill connected={jobtreadConnected} label="JobTread" />
          {!showConnectHero && (
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            title="Timeframe"
            className={`px-3 py-1.5 rounded-lg border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-500 ${
              darkMode
                ? 'bg-gray-800 border-gray-700 text-gray-200'
                : 'bg-white border-gray-200 text-gray-700'
            }`}
          >
            {MARKETING_TIMEFRAMES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
          )}
          {jobtreadConnected && (
            <button
              onClick={openLeadSettings}
              title="Lead settings — choose what counts as a lead and the lead-source field"
              className={`p-2 rounded-lg border ${
                darkMode
                  ? 'bg-gray-800 border-gray-700 text-gray-300 hover:text-white'
                  : 'bg-white border-gray-200 text-gray-600 hover:text-gray-900'
              }`}
            >
              <Settings className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {devMode && <DevModePanel debug={debug} page="marketing" />}

      {!dataLoading && jobtreadConnected && jobtreadError && (
        <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200 px-4 py-3 rounded-lg mb-6 text-sm">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">JobTread data couldn&apos;t be loaded</p>
            <p className="mt-0.5">{jobtreadError}</p>
          </div>
        </div>
      )}

      {showConnectHero ? (
        <ConnectJobTreadHero
          description={JT_HERO_DESCRIPTION}
          clientUserId={userId}
          onConnected={refresh}
        />
      ) : (
      <>
      {/* Left 3/4: tall Leads chart · Right 1/4: Qualified Lead % / Top Lead Source / Lead Sources */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-5 mb-6">
        <div className="xl:col-span-3 flex flex-col [&>div]:flex-1">
          <MetricWidget
            stretchChart
            title="Leads"
            titleMeta={leadsMeta || undefined}
            icon={<FaUsers />}
            value={m.leads.value}
            valueStatus="none"
            source={m.leads.source}
            loading={dataLoading}
            empty={m.leads.source === 'none'}
            emptyText="Connect JobTread or add monthly lead counts to track pipeline."
            trace={devMode ? m.leads.trace : null}
            showSourceBadge={false}
            headerAction={
              m.leads.source === 'jobtread' ? (
                <JtPill />
              ) : showLeadsManualEntry ? (
                <button
                  onClick={() => setShowAddPopup(true)}
                  title="Add leads entry"
                  className={`p-1 rounded-full ${darkMode ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-100 hover:bg-red-200 text-red-600'}`}
                >
                  <FaPlus className="w-3 h-3" />
                </button>
              ) : undefined
            }
          >
            {leadsSeries.length > 0 && (
              <>
                <div className="flex-1 min-h-[16rem]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={leadsSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="marketingLeadsFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={LEADS_COLOR} stopOpacity={0.25} />
                          <stop offset="95%" stopColor={LEADS_COLOR} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={darkMode ? '#374151' : '#e5e7eb'} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: darkMode ? '#9ca3af' : '#6b7280' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 11, fill: darkMode ? '#9ca3af' : '#6b7280' }}
                        axisLine={false}
                        tickLine={false}
                        width={36}
                      />
                      <Tooltip
                        formatter={(v, name) => [Number(v).toLocaleString(), name]}
                        contentStyle={
                          darkMode
                            ? { backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8, color: '#f9fafb' }
                            : { borderRadius: 8 }
                        }
                      />
                      <Area
                        type="monotone"
                        dataKey="value"
                        name="Leads"
                        stroke={LEADS_COLOR}
                        strokeWidth={2}
                        fill="url(#marketingLeadsFill)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center mt-2 text-sm text-gray-600 dark:text-gray-300">
                  <span className="flex items-center gap-2 font-medium">
                    <span className="w-3 h-3 rounded-full bg-red-600 shrink-0" />
                    Leads
                  </span>
                </div>
              </>
            )}
            {showLeadsManualEntry && leadsEntries.length > 0 && (
              <button
                onClick={() => setShowEditPopup(true)}
                className="text-xs text-red-600 hover:text-red-700 hover:underline mt-2"
              >
                Manage entries
              </button>
            )}
          </MetricWidget>
        </div>

        {/* Right column: Top Lead Sources — stretches to the chart height */}
        <div className="flex flex-col gap-5 [&>div]:flex-1">
          <MetricWidget
            stretchChart
            title="Top Lead Sources"
            icon={<Award className="w-4 h-4" />}
            value={null}
            valueStatus="none"
            source={m.leadSources.source}
            loading={dataLoading}
            showSourceBadge={false}
            headerAction={m.leadSources.source === 'jobtread' ? <JtPill /> : undefined}
            empty={m.leadSources.source === 'none'}
            emptyText={
              jobtreadConnected
                ? 'No lead-source values in this window — pick the JobTread field that holds your lead source.'
                : 'Connect JobTread to see where your leads come from.'
            }
            emptyAction={jobtreadConnected ? <LeadSettingsButton onClick={openLeadSettings} label="Choose lead-source field" /> : undefined}
            trace={devMode ? m.leadSources.trace : null}
            footer={
              m.leadSources.rawValue != null
                ? `${m.leadSources.attributed} of ${m.leadSources.attributed + m.leadSources.unattributed} lead${m.leadSources.attributed + m.leadSources.unattributed === 1 ? '' : 's'} attributed`
                : undefined
            }
          >
            {m.leadSources.sources?.length > 0 && (
              <TopLeadSources
                sources={m.leadSources.sources}
                attributed={m.leadSources.attributed}
              />
            )}
          </MetricWidget>
        </div>
      </div>

      {/* Leads Needed — broken down across the full row */}
      <div className="rounded-xl border bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 shadow-sm p-5 mb-6">
        <div className="flex items-start justify-between gap-2 mb-4">
        <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
          <span className="text-red-600"><Target className="w-4 h-4" /></span>
          <h3 className="font-semibold text-gray-800 dark:text-white">Leads Needed</h3>
          {!editingRevenue ? (
            <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              to hit your{' '}
              {revenueGoalValue != null ? (
                <span className="font-semibold text-gray-700 dark:text-gray-200">${Math.round(revenueGoalValue).toLocaleString()}</span>
              ) : (
                <span className="italic">(set a)</span>
              )}{' '}
              revenue goal
              <button
                type="button"
                onClick={() => {
                  setRevenueDraft(revenueGoalValue != null ? String(Math.round(revenueGoalValue)) : '');
                  setEditingRevenue(true);
                }}
                title="Edit revenue goal"
                className="p-0.5 rounded text-gray-400 hover:text-red-600"
              >
                <Pencil className="w-3 h-3" />
              </button>
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500 dark:text-gray-400">to hit your</span>
              <div className="relative w-32">
                <span className={`absolute left-2.5 top-1/2 -translate-y-1/2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>$</span>
                <input
                  type="number"
                  value={revenueDraft}
                  onChange={(e) => setRevenueDraft(e.target.value)}
                  min="0"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveRevenue(); }}
                  className={`w-full pl-6 pr-2 py-1 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">revenue goal</span>
              <button onClick={handleSaveRevenue} disabled={revenueSaving} className="px-2.5 py-1 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 disabled:opacity-50">
                {revenueSaving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setEditingRevenue(false)} className={`px-2.5 py-1 rounded-lg text-xs font-medium ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}>
                Cancel
              </button>
            </span>
          )}
        </div>
          <JtPill connected={jobtreadConnected} />
        </div>
        {leadsNeeded ? (
          <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-gray-100 dark:divide-gray-700">
            {[
              { label: 'Annually', value: leadsNeeded.leadsNeededAnnually, actual: leadsActual?.annual },
              { label: 'Quarterly', value: leadsNeeded.leadsNeededQuarterly, actual: leadsActual?.quarter },
              { label: 'Monthly', value: leadsNeeded.leadsNeededMonthly, actual: leadsActual?.month },
              { label: 'Weekly', value: leadsNeeded.leadsNeededWeekly, actual: leadsActual?.week }
            ].map(({ label, value, actual }) => {
              const hit = actual != null && actual >= value;
              const pct = actual != null && value > 0 ? Math.min(100, Math.round((actual / value) * 100)) : 0;
              return (
                <div key={label} className="flex flex-col items-center text-center py-3 md:py-0 px-3">
                  <span className="text-3xl font-bold text-gray-800 dark:text-white">{value.toLocaleString()}</span>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-1">{label}</span>
                  {actual != null && (
                    <div className="w-full mt-2">
                      <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${hit ? 'bg-emerald-500' : 'bg-red-500'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className={`block text-[11px] font-medium mt-1 ${hit ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500'}`}>
                        {actual.toLocaleString()} of {value.toLocaleString()}{hit ? ' ✓' : ''}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center text-center py-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              {effectiveQualified == null
                ? 'Set your qualified lead % by mapping a qualified field in JobTread to see how many leads you need.'
                : 'Needs revenue, average job size, and close ratio from your sales data — connect JobTread to fill them in.'}
            </p>
            {effectiveQualified == null && jobtreadConnected && (
              <LeadSettingsButton onClick={openLeadSettings} label="Map qualified field" />
            )}
          </div>
        )}
      </div>

      {/* Channel totals (click anywhere on a card to update) + Qualified Lead % */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
        {[
          { key: 'socialFollowers', title: 'Social Media', icon: <Share2 className="w-4 h-4" />, metric: m.social, sub: 'Total followers', money: false, emptyText: 'Click to add your total followers.' },
          { key: 'paidSpend', title: 'Paid Media', icon: <Megaphone className="w-4 h-4" />, metric: m.paidMedia, sub: 'Total ad spend', money: true, emptyText: 'Click to add your total ad spend.' },
          { key: 'newsletterSubscribers', title: 'Newsletter', icon: <Mail className="w-4 h-4" />, metric: m.newsletter, sub: 'Total subscribers', money: false, emptyText: 'Click to add your total subscribers.' }
        ].map(({ key, title, icon, metric, sub, money, emptyText }) => {
          const editing = editingChannel === key;
          const startEdit = () => {
            setChannelDraft(metric.rawValue != null ? String(Math.round(metric.rawValue)) : '');
            setEditingChannel(key);
          };
          return (
            <div
              key={key}
              onClick={!editing ? startEdit : undefined}
              className={!editing ? 'cursor-pointer' : ''}
            >
            <MetricWidget
              compact
              title={title}
              icon={icon}
              value={editing ? null : metric.value}
              valueStatus="none"
              source={metric.source}
              sub={editing ? undefined : sub}
              loading={dataLoading}
              empty={metric.source === 'none' && !editing}
              emptyText={emptyText}
              showSourceBadge={false}
              trace={devMode ? metric.trace : null}
              headerAction={
                !editing ? (
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      darkMode
                        ? 'bg-red-900/30 text-red-300'
                        : 'bg-red-50 text-red-600'
                    }`}
                  >
                    Click to update
                  </span>
                ) : undefined
              }
            >
              {editing && (
                <div className="flex items-center gap-2">
                  <div className="relative w-28">
                    {money && (
                      <span className={`absolute left-2.5 top-1/2 -translate-y-1/2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>$</span>
                    )}
                    <input
                      type="number"
                      value={channelDraft}
                      onChange={(e) => setChannelDraft(e.target.value)}
                      min="0"
                      autoFocus
                      className={`w-full ${money ? 'pl-6' : 'pl-3'} pr-3 py-1.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                  <button
                    onClick={() => handleSaveChannel(key)}
                    disabled={channelSaving}
                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 disabled:opacity-50"
                  >
                    {channelSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={() => { setEditingChannel(null); setChannelDraft(''); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </MetricWidget>
            </div>
          );
        })}

        {/* Qualified Lead % — from a mapped JobTread field when available,
            else the value your BBA team set via the CRM leads calculator.
            Feeds the Leads Needed breakdown above. */}
        <MetricWidget
          compact
          title="Qualified Lead %"
          icon={<Percent className="w-4 h-4" />}
          value={effectiveQualified != null ? `${effectiveQualified}%` : null}
          valueStatus="none"
          source={qualified?.source === 'jobtread' ? 'jobtread' : 'manual'}
          sub={
            qualified?.source === 'jobtread'
              ? `${qualified.qualifiedCount} of ${qualified.answered} answered qualified${qualified.unanswered > 0 ? ` · ${qualified.unanswered} not marked` : ''}`
              : 'Share of your leads worth a proposal'
          }
          loading={dataLoading}
          empty={effectiveQualified == null}
          emptyText={
            qualified?.source === 'jobtread'
              ? 'No leads have a qualified yes/no answer yet — mark them in JobTread.'
              : 'Map the JobTread field that marks a lead as qualified, or your BBA team sets this with you.'
          }
          emptyAction={jobtreadConnected ? <LeadSettingsButton onClick={openLeadSettings} label={qualified?.source === 'jobtread' ? 'Change qualified field' : 'Map qualified field'} /> : undefined}
          showSourceBadge={false}
          headerAction={qualified?.source === 'jobtread' ? <JtPill /> : undefined}
        >
          {qualifiedNotice && (
            <div className="mt-2 flex items-start gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
              <span>{qualifiedNotice}</span>
            </div>
          )}
        </MetricWidget>
      </div>
      </>
      )}

      {/* Lead settings: what counts as a lead + lead-source custom field */}
      {showLeadSettings && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto`}>
            <div className="flex items-center mb-4 relative">
              <h3 className={`text-lg font-semibold flex-1 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>Lead Settings</h3>
              <button onClick={() => setShowLeadSettings(false)} className={`p-1 rounded-full absolute right-0 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* 1. What counts as a lead — New Job vs New Customer, side by side */}
              <div>
                <h4 className={`text-sm font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  What counts as a lead in JobTread?
                </h4>
                <p className={`text-xs mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  This drives the Leads chart and lead-source attribution.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: 'job', label: 'New Job', desc: 'Each JobTread job created' },
                    { id: 'customer', label: 'New Customer', desc: 'Each customer account created' }
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => chooseEntity(opt.id)}
                      className={`text-left p-3 rounded-lg border transition-colors ${
                        ldEntity === opt.id
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/10'
                          : darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className={`block text-sm font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{opt.label}</span>
                      <span className={`block text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 2. Scope — all created OR in specific status */}
              <div>
                <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Which {ldEntity === 'customer' ? 'customers' : 'jobs'} count?
                </h4>
                <div className="space-y-2">
                  {[
                    { id: 'all', label: `All ${ldEntity === 'customer' ? 'customers' : 'jobs'} created`, desc: `Every new ${ldEntity} counts as a lead.` },
                    { id: 'status', label: `New ${ldEntity} in specific status`, desc: 'Count only those whose status field is in your selection.' }
                  ].map((opt) => (
                    <label
                      key={opt.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                        ldScope === opt.id
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/10'
                          : darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="lead-scope"
                        checked={ldScope === opt.id}
                        onChange={() => setLdScope(opt.id)}
                        className="mt-0.5 w-4 h-4 text-red-600 focus:ring-red-500"
                      />
                      <span className="min-w-0">
                        <span className={`block text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{opt.label}</span>
                        <span className={`block text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{opt.desc}</span>
                      </span>
                    </label>
                  ))}
                </div>

                {ldScope === 'status' && (
                  <div className={`mt-3 p-3 rounded-xl border border-gray-200 dark:border-gray-800`}>
                    <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Status field
                    </label>
                    <select
                      value={ldStatusFieldId}
                      onChange={(e) => { setLdStatusFieldId(e.target.value); setLdStatuses([]); }}
                      className={`w-full p-2 border rounded-lg text-sm mb-1 focus:outline-none focus:ring-2 focus:ring-red-500 ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      {ldEntity === 'job' && <option value="">Status (built-in)</option>}
                      {ldEntity === 'customer' && entityCustomFields.length === 0 && (
                        <option value="" disabled>{fieldsLoading ? 'Loading fields…' : 'No customer fields found'}</option>
                      )}
                      {entityCustomFields.map((f) => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                    {fieldsLoading && (
                      <p className={`text-[11px] mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Loading your JobTread custom fields…
                      </p>
                    )}
                    <div className="mb-2" />

                    <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Count these statuses as leads
                    </label>
                    {!entityMatchesLoaded ? (
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Save to load the available statuses for {ldEntity === 'customer' ? 'customers' : 'jobs'}.
                      </p>
                    ) : statusValueOptions.length === 0 ? (
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        No values found on the selected field for your {ldEntity === 'customer' ? 'customers' : 'jobs'}.
                      </p>
                    ) : (
                      <div className="space-y-1.5 max-h-44 overflow-y-auto">
                        {statusValueOptions.map(({ value, count }) => {
                          const key = value.toLowerCase();
                          return (
                            <label key={value} className="flex items-center justify-between gap-2 text-sm cursor-pointer">
                              <span className="flex items-center gap-2 min-w-0">
                                <input
                                  type="checkbox"
                                  checked={ldStatuses.includes(key)}
                                  onChange={(e) =>
                                    setLdStatuses((prev) =>
                                      e.target.checked ? [...prev, key] : prev.filter((s) => s !== key)
                                    )
                                  }
                                  className="w-4 h-4 rounded text-red-600 focus:ring-red-500"
                                />
                                <span className={`truncate ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{value}</span>
                              </span>
                              <span className={`text-xs shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{count}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 3. Lead source field — the chosen entity's custom fields */}
              <div>
                <h4 className={`text-sm font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Lead source field
                </h4>
                <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  The {ldEntity === 'customer' ? 'customer' : 'job'} custom field that records where each lead came from — powers Top Lead Sources.
                </p>
                <select
                  value={ldSourceFieldId}
                  onChange={(e) => setLdSourceFieldId(e.target.value)}
                  className={`w-full p-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="">Not set</option>
                  {entityCustomFields.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
                {fieldsLoading ? (
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Loading your JobTread custom fields…
                  </p>
                ) : entityCustomFields.length === 0 && (
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No {ldEntity === 'customer' ? 'customer' : 'job'} custom fields found in your JobTread organization.
                  </p>
                )}
              </div>

              {/* 4. Qualified lead field — the entity's boolean custom field */}
              <div>
                <h4 className={`text-sm font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Qualified lead field
                </h4>
                <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  The {ldEntity === 'customer' ? 'customer' : 'job'} yes/no custom field marking a lead as qualified — sets your Qualified Lead % from real data.
                </p>
                <select
                  value={ldQualifiedFieldId}
                  onChange={(e) => setLdQualifiedFieldId(e.target.value)}
                  className={`w-full p-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="">Not set</option>
                  {entityCustomFields.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex space-x-3 pt-6">
              <button
                type="button"
                onClick={() => setShowLeadSettings(false)}
                className={`flex-1 py-2 px-4 rounded-lg border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveLeadSettings}
                disabled={leadSettingsSaving}
                className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {leadSettingsSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-md`}>
            <div className="flex items-center mb-4 relative">
              <h3 className={`text-lg font-semibold flex-1 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>Add Leads Entry</h3>
              <button onClick={() => setShowAddPopup(false)} className={`p-1 rounded-full absolute right-0 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleAddEntry} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Leads Count</label>
                <input
                  type="number"
                  value={formData.leadsCount}
                  onChange={(e) => setFormData({ ...formData, leadsCount: e.target.value })}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="Enter leads count"
                  min="0"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Month</label>
                <select
                  value={formData.month}
                  onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  {months.map((month) => <option key={month} value={month}>{month}</option>)}
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Year</label>
                <select
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  {years.map((year) => <option key={year} value={year}>{year}</option>)}
                </select>
              </div>
              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={() => setShowAddPopup(false)} className={`flex-1 py-2 px-4 rounded-lg border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>Cancel</button>
                <button type="submit" className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700">Add Entry</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto`}>
            <div className="flex items-center mb-4 relative">
              <h3 className={`text-lg font-semibold flex-1 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>Manage Leads Entries</h3>
              <button onClick={() => { setShowEditPopup(false); setEditingEntry(null); }} className={`p-1 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {editingEntry && (
              <div className={`p-4 mb-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                <h4 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Edit Entry</h4>
                <form onSubmit={handleUpdateEntry} className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Leads</label>
                    <input type="number" value={formData.leadsCount} onChange={(e) => setFormData({ ...formData, leadsCount: e.target.value })} className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-500 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`} min="0" />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Month</label>
                    <select value={formData.month} onChange={(e) => setFormData({ ...formData, month: e.target.value })} className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-500 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                      {months.map((month) => <option key={month} value={month}>{month}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Year</label>
                    <select value={formData.year} onChange={(e) => setFormData({ ...formData, year: e.target.value })} className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-500 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                      {years.map((year) => <option key={year} value={year}>{year}</option>)}
                    </select>
                  </div>
                  <div className="flex items-end space-x-2">
                    <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">Update</button>
                    <button type="button" onClick={() => setEditingEntry(null)} className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600">Cancel</button>
                  </div>
                </form>
              </div>
            )}

            <div className="space-y-2">
              <h4 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>All Entries</h4>
              {leadsEntries.length === 0 ? (
                <p className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No entries found. Add your first entry using the + button.</p>
              ) : (
                leadsEntries
                  .slice()
                  .sort((a, b) => (b.year - a.year) || (months.indexOf(b.month) - months.indexOf(a.month)))
                  .map((entry) => (
                    <div key={entry.id} className={`flex items-center justify-between p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                      <div className="flex items-center space-x-4">
                        <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>{entry.count}</span>
                        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{entry.month} {entry.year}</span>
                      </div>
                      <div className="flex space-x-2">
                        <button onClick={() => handleEditEntry(entry)} className={`p-1 rounded ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button onClick={() => handleDeleteEntry(entry.id)} className="p-1 rounded text-red-500 hover:bg-red-100 dark:hover:bg-red-900">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingAnalyticsTab;
